import React, { useEffect, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";

import { fetchAllCauses } from "store/causes/causes.actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { CauseId } from "types";
import { v4 as uuid } from "uuid";

import { BorderProps } from "components/common/LocationFields/LocationFields.types";
import {
  CauseCard,
  CauseName,
  CausesContainer,
  RenderView,
  SelectedCauses,
  SelectedContainer,
  SelectedLabel,
  StyledImg,
  Thumb,
  Track
} from "./CausesSelector.styles";

interface CausesSelectorProps {
  selectedCauses: CauseId[];
  setSelectedCauses: (causes: CauseId[]) => void;
  disabled?: boolean;
  border?: BorderProps;
  maxWidth?: string;
}

const CausesSelector: React.FC<CausesSelectorProps> = ({ selectedCauses, setSelectedCauses, disabled = false, border, maxWidth }) => {
  const [trailPosition, setTrailPosition] = useState(0);
  const causes = useAppSelector((state) => state.causes.data);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (causes.length === 0) {
      dispatch(fetchAllCauses());
    }
  }, []);

  const handleSelection = (causeId: CauseId) => {
    if (disabled) return;

    // Check if the cause is already selected
    if (selectedCauses.includes(causeId)) {
      // If already selected, remove it (toggle off)
      setSelectedCauses(selectedCauses.filter(id => id !== causeId));
    } else {
      // If not selected, add it (toggle on)
      setSelectedCauses([...selectedCauses, causeId]);
    }
  };

  const handleScrollFrame = (e: { left: number }) => {
    setTrailPosition(e.left);
  };

  return (
    <CausesContainer border={border} maxWidth={maxWidth}>
      <SelectedContainer>
        {selectedCauses.length > 0 ? (
          <SelectedLabel>Selected: </SelectedLabel>
        ) : (
          <SelectedLabel>No causes selected</SelectedLabel>
        )}
        {selectedCauses.map((selectedId) => {
          const foundCause = causes.find((cause) => cause.id === selectedId);
          if (!foundCause) return null;
          return (
            <SelectedCauses key={uuid()}>
              {foundCause.cause_name}
            </SelectedCauses>
          );
        })}
      </SelectedContainer>
      <Scrollbars
        style={{ overflow: "visible" }}
        renderView={({ style, ...props }: any) => <RenderView {...props} />}
        renderThumbHorizontal={({ style, ...props }: any) => (
          <Thumb
            {...props}
            style={{
              left: `${100 - 100 * trailPosition < 16.08 ? 10 : 0}px`,
            }}
          />
        )}
        renderTrackHorizontal={({ style, ...props }: any) => (
          <Track
            {...props}
            $trailPosition
            style={{
              background: `linear-gradient(to left, white ${100 - 100 * trailPosition < 81
                ? 100 - 100 * trailPosition + 1
                : 100 - 100 * trailPosition
                }%, #FE2F67 0%)`,
            }}
          />
        )}
        thumbSize={20}
        onScrollFrame={(e: { left: number }) => handleScrollFrame(e)}
      >
        {causes.map((cause) => (
          <CauseCard key={uuid()}>
            <StyledImg
              src={cause.cause_image}
              width="170"
              height="125"
              style={{
                outline: selectedCauses.includes(cause.id) ? "10px solid pink" : "",
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
              onClick={() => handleSelection(cause.id)}
            />
            <CauseName>{cause.cause_name}</CauseName>
          </CauseCard>
        ))}
      </Scrollbars>
    </CausesContainer>
  );
};

export default CausesSelector; 